import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { MediaStage } from '@/components/features/media/MediaStage'
import { sanitizeHTML } from '@/lib/utils'
import { rewriteHtmlImages } from '@/lib/images'

interface RichHtmlProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'dangerouslySetInnerHTML'> {
  /** Rich text from the editor; sanitized here. */
  html: string | null | undefined
}

type Viewer = { src: string; alt: string }

/* Share of the viewport an inline image may take before it is cropped. */
const DESKTOP_LIMIT = 0.7
const MOBILE_LIMIT = 0.6
const DESKTOP_QUERY = '(min-width: 768px)'

const heightLimit = () =>
  window.innerHeight * (window.matchMedia(DESKTOP_QUERY).matches ? DESKTOP_LIMIT : MOBILE_LIMIT)

const TRANSPARENT = /^(transparent|rgba\(\s*0,\s*0,\s*0,\s*0\s*\))$/

/** The color the text sits on (card, dialog or page), so the fade blends into whatever theme is around it. */
const surfaceColor = (from: Element): string => {
  for (let el: Element | null = from; el; el = el.parentElement) {
    const color = getComputedStyle(el).backgroundColor
    if (color && !TRANSPARENT.test(color)) return color
  }
  return '#fff'
}

const TOGGLE_BASE =
  'rich-tall-toggle z-[1] items-center rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'

type Labels = { expand: string; collapse: string; open: string }

/** Sets the wrapper of one tall image to collapsed, expanded or fits (no longer taller than the limit). */
const applyState = (wrapper: HTMLElement, state: 'collapsed' | 'expanded' | 'fits', labels: Labels) => {
  wrapper.dataset.richTall = state
  const frame = wrapper.querySelector<HTMLElement>('.rich-tall-frame')
  const fade = wrapper.querySelector<HTMLElement>('.rich-tall-fade')
  const toggle = wrapper.querySelector<HTMLButtonElement>('.rich-tall-toggle')
  if (!frame || !fade || !toggle) return
  frame.style.maxHeight = state === 'collapsed' ? 'var(--rich-tall-max)' : ''
  /* Inline display, not `hidden`: the `block`/`flex` utilities would override the attribute. */
  fade.style.display = state === 'collapsed' ? '' : 'none'
  toggle.style.display = state === 'fits' ? 'none' : ''
  toggle.setAttribute('aria-expanded', String(state === 'expanded'))
  toggle.textContent = state === 'expanded' ? labels.collapse : labels.expand
  toggle.className = state === 'expanded'
    ? `${TOGGLE_BASE} relative mx-auto mt-3 flex w-fit`
    : `${TOGGLE_BASE} absolute bottom-4 left-1/2 inline-flex -translate-x-1/2`
}

/**
 * Rich text (posts, lessons, events, descriptions). An inline image taller than 70% of the viewport
 * (60% on phones) at its rendered width shows cropped from the top, fading into the surface below,
 * with a button to expand it in place; clicking the image opens it full screen. Shorter images are
 * left as they are. Images are measured once loaded (or right away when the HTML gives their size),
 * before the browser paints them, so the crop does not move the text.
 */
/** Inline images: the widest rich-text column, with a 2x srcset. */
const INLINE_IMAGE = { w: 800, fit: 'max' } as const
/** Full-screen viewer: large, but still a fraction of an original photo. */
const VIEWER_IMAGE = { w: 2000, fit: 'max' } as const

export const RichHtml = ({ html, ...props }: RichHtmlProps) => {
  const { t } = useTranslation('common')
  const ref = React.useRef<HTMLDivElement>(null)
  const [viewer, setViewer] = React.useState<Viewer | null>(null)
  /* Platform images go through imgix at the column width (rich text never runs wider than ~800px);
     the stored HTML keeps the origin URLs. */
  const clean = React.useMemo(() => rewriteHtmlImages(sanitizeHTML(html || ''), INLINE_IMAGE), [html])
  /* Read at click and resize time: a language switch must not rebuild (and unwire) the wrappers. */
  const labelsRef = React.useRef<Labels>({ expand: '', collapse: '', open: '' })
  labelsRef.current = { expand: t('richText.showFullImage'), collapse: t('richText.collapseImage'), open: t('richText.openImage') }

  React.useLayoutEffect(() => {
    const root = ref.current
    if (!root) return
    const labels = labelsRef.current
    const cleanups: Array<() => void> = []

    const wrap = (img: HTMLImageElement) => {
      const style = getComputedStyle(img)
      const wrapper = document.createElement('span')
      wrapper.className = 'rich-tall relative block w-fit max-w-full'
      wrapper.style.marginTop = style.marginTop
      wrapper.style.marginBottom = style.marginBottom
      wrapper.style.setProperty('--rich-tall-fade', surfaceColor(img.parentElement ?? root))

      const frame = document.createElement('span')
      frame.className = 'rich-tall-frame relative block w-fit max-w-full overflow-hidden'
      img.replaceWith(wrapper)
      wrapper.appendChild(frame)
      img.style.margin = '0'

      /* A linked image keeps its link; any other opens the viewer. */
      if (img.closest('a')) {
        frame.appendChild(img)
      } else {
        const open = document.createElement('button')
        open.type = 'button'
        open.className = 'rich-tall-open block w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset'
        open.setAttribute('aria-haspopup', 'dialog')
        open.setAttribute('aria-label', img.alt ? `${labels.open}: ${img.alt}` : labels.open)
        open.appendChild(img)
        frame.appendChild(open)
        const onOpen = () => setViewer({ src: img.dataset.origin || img.currentSrc || img.src, alt: img.alt })
        open.addEventListener('click', onOpen)
        cleanups.push(() => open.removeEventListener('click', onOpen))
      }

      const fade = document.createElement('span')
      fade.className = 'rich-tall-fade pointer-events-none absolute inset-x-0 bottom-0 block h-32'
      fade.style.background = 'linear-gradient(to bottom, transparent, var(--rich-tall-fade))'
      fade.setAttribute('aria-hidden', 'true')
      frame.appendChild(fade)

      const toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.className = TOGGLE_BASE
      wrapper.appendChild(toggle)
      const onToggle = () => {
        const expanding = wrapper.dataset.richTall !== 'expanded'
        applyState(wrapper, expanding ? 'expanded' : 'collapsed', labelsRef.current)
        if (!expanding && wrapper.getBoundingClientRect().top < 0) wrapper.scrollIntoView({ block: 'start' })
      }
      toggle.addEventListener('click', onToggle)
      cleanups.push(() => toggle.removeEventListener('click', onToggle))
      return wrapper
    }

    /* Rendered height at the current width: the frame clips, the image itself keeps its full height. */
    const measure = (img: HTMLImageElement) => {
      const height = img.getBoundingClientRect().height
      if (height <= 0) return
      const limit = heightLimit()
      const tall = height > limit
      const existing = img.closest<HTMLElement>('.rich-tall')
      if (!existing && !tall) return
      const wrapper = existing ?? wrap(img)
      wrapper.style.setProperty('--rich-tall-max', `${Math.round(limit)}px`)
      const expanded = wrapper.dataset.richTall === 'expanded'
      applyState(wrapper, !tall ? 'fits' : expanded ? 'expanded' : 'collapsed', labelsRef.current)
    }

    const images = Array.from(root.querySelectorAll('img'))
    images.forEach((img) => {
      const sized = img.hasAttribute('width') && img.hasAttribute('height')
      if ((img.complete && img.naturalHeight > 0) || sized) measure(img)
      if (!img.complete) {
        const onLoad = () => measure(img)
        img.addEventListener('load', onLoad, { once: true })
        cleanups.push(() => img.removeEventListener('load', onLoad))
      }
    })

    let frameId = 0
    const onResize = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => images.forEach((img) => img.isConnected && measure(img)))
    }
    window.addEventListener('resize', onResize)
    cleanups.push(() => {
      window.removeEventListener('resize', onResize)
      cancelAnimationFrame(frameId)
    })
    return () => cleanups.forEach((fn) => fn())
  }, [clean])

  return (
    <>
      <div ref={ref} {...props} dangerouslySetInnerHTML={{ __html: clean }} />
      <Dialog open={!!viewer} onOpenChange={(open) => { if (!open) setViewer(null) }}>
        <DialogContent className="max-w-6xl overflow-hidden bg-black p-0" data-testid="rich-image-viewer">
          <VisuallyHidden>
            <DialogTitle>{viewer?.alt || t('richText.imageViewer')}</DialogTitle>
            <DialogDescription>{t('richText.imageViewer')}</DialogDescription>
          </VisuallyHidden>
          <div className="relative h-[90dvh] w-full">
            {viewer && <MediaStage imageUrl={viewer.src} cdn={VIEWER_IMAGE} alt={viewer.alt} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
